import { Config } from '../video-data/config';
import type { Anchor, Position, CurvePointDescription, RemoveAnchoredPoints } from './Curve';
import { Curve } from './Curve';

export type LinearCurveDescription = {
    classType: 'LinearCurve';
    duration?: number;
    points?: CurvePointDescription[];
    removeAnchoredPoints?: RemoveAnchoredPoints;
};

export class LinearCurve extends Curve {
    add(p: Position, anchor: Anchor = Config.MIDDLE_ANIMATION_CURVE_ANCHOR) {
        const ep = this._addEndPoint(p);
        ep.anchor = anchor;
    }

    _getEndPointIndexForPercentage(value: number): number | undefined {
        const endPoints = this.getEndPoints();
        const nEndPoints = endPoints.length;

        if (value <= 0.0) {
            return 0;
        } else if (value >= 1.0) {
            return nEndPoints - 2;
        } else {
            if (value < endPoints[0].x) {
                return 0;
            } else if (value >= endPoints[nEndPoints - 1].x) {
                return nEndPoints - 2;
            } else {
                for (let i = 0; i < nEndPoints - 1; i++) {
                    if (endPoints[i].x <= value && value <= endPoints[i + 1].x) {
                        return i;
                    }
                }
            }
        }
    }

    _evalPercentage(value: number) {
        const index = this._getEndPointIndexForPercentage(value);
        if (index === undefined) {
            throw Error(`Invalid index ${index} for value ${value}.`);
        }

        const points = this.getPositions();
        const p0 = points[index];
        const p1 = points[index + 1];

        // NOTE: use the value of the first point if we don't have any points before the requested value.
        // this happens when we move the curve's points to the right.
        if (value < p0.x) {
            return p0.y;
        }
        // NOTE: use the value of the last point if we don't have any points after the requested value.
        // this happens when we move the curve's points to the left.
        else if (value > p1.x) {
            return p1.y;
        } else {
            const delta = p1.x - p0.x;
            const relValue = (value - p0.x) / delta;
            return p0.y + (p1.y - p0.y) * relValue;
        }
    }

    /**
     * Overlapping curves cannot be properly evaluated.
     * This method will check if provided data results in an overlap.
     */
    _isDataValid(): boolean {
        // return falls if not all points are increasing in x value
        const pointsX = this.points.map((point) => point.x);
        const isIncreasing = pointsX.every((number, i) => i === 0 || pointsX[i - 1] <= number);
        if (!isIncreasing) {
            return false;
        }
        // return false if two points have the same x value
        for (const evalPoint of this.points) {
            const overlappingPoints = this.points.filter(
                (curvePoint) => curvePoint.x === evalPoint.x,
            );
            if (overlappingPoints.length > 1) {
                return false;
            }
        }
        return true;
    }

    /**
     * Attempts to correct matching values in an array, maintaining a 0 to 1 boundary.
     * @returns true if array was corrected
     */
    correctData(): boolean {
        let dataCorrected = false;
        // check if two points have the same x value
        this.points.forEach((evalPoint) => {
            const overlappingPoints = this.points.filter(
                (curvePoint) => curvePoint.x === evalPoint.x,
            );
            if (overlappingPoints.length > 1) {
                // if two points are equal in x
                if (overlappingPoints[0].x === overlappingPoints[1].x) {
                    // when 0, nudge the second one forward to break equality.
                    // Otherwise, nudge the first one back to break equality.
                    if (overlappingPoints[0].x === 0) {
                        overlappingPoints[1].x = overlappingPoints[1].x * 1.001;
                    } else {
                        overlappingPoints[0].x = overlappingPoints[0].x * 0.999;
                    }
                    dataCorrected = true;
                }
            }
        });
        return dataCorrected;
    }

    override setCurveCoordinates(coordinatesString: string) {
        this.clearPoints();
        const tokens = coordinatesString.slice(2, coordinatesString.length).split(' L ');
        const coordinates = [];

        for (let i = 0; i < tokens.length; i++) {
            const stokens = tokens[i].split(' ');
            coordinates.push({
                x: parseFloat(stokens[0]),
                y: parseFloat(stokens[1]),
            });
        }

        coordinates.forEach((coordinate) => {
            this.add(coordinate);
        });
    }

    override getCurveCoordinates(precision = 5): string {
        let c = '';
        const points = this.getEndPoints();
        const np = points.length;
        for (let i = 0; i < np; i++) {
            c += i === 0 ? 'M ' : ' L ';
            c += `${parseFloat(points[i].x.toFixed(precision))} ${parseFloat(points[i].y.toFixed(precision))}`;
        }
        return c;
    }

    override getCurveDescription(precision = 5): LinearCurveDescription {
        return {
            duration: this.duration,
            classType: 'LinearCurve',
            points: this.getEndPoints().map((endPoint) => ({
                anchor: endPoint.anchor,
                position: endPoint.getPosition(precision),
            })),
        };
    }

    override setCurveDescription(data: LinearCurveDescription) {
        this.clearPoints();

        if (data.hasOwnProperty('duration')) {
            // directly set duration without scaling end points.
            this._duration = data.duration!;
        }

        const pointData = data.points ?? [];

        for (let i = 0; i < pointData.length; i++) {
            let anchor: Anchor = Config.MIDDLE_ANIMATION_CURVE_ANCHOR;
            if (pointData[i].hasOwnProperty('anchor')) {
                anchor = pointData[i].anchor!;
            }

            const px = pointData[i].position.x;
            const py = pointData[i].position.y;
            const p = { x: px, y: py };
            this.add(p, anchor);
        }

        // try to automatically assign anchors when none are provided
        if (!this.hasBoundaryAnchors()) {
            this.assignBoundaryAnchors();
        }

        if (data.removeAnchoredPoints !== Config.NONE_ANIMATION_CURVE_ANCHOR) {
            this.removePointsWithAnchor(data.removeAnchoredPoints!);
        }
    }

    static fromDescription(description: LinearCurveDescription): LinearCurve {
        const instance = new LinearCurve();
        instance.setCurveDescription(description);
        return instance;
    }

    static raisingDescription(duration?: number): LinearCurveDescription {
        const instance = new LinearCurve();
        instance.add({ x: 0, y: 0 }, Config.START_ANIMATION_CURVE_ANCHOR);
        instance.add({ x: 1, y: 1 }, Config.END_ANIMATION_CURVE_ANCHOR);

        const description = instance.getCurveDescription();
        if (duration !== undefined) {
            description.duration = duration;
        }
        return description;
    }

    /**
     * Creates a curve from target values
     */
    static descriptionFromTargetValues(
        targets: { value: number[]; time: number[] },
        duration: number,
    ) {
        const minValue = Math.min(...targets.value);
        const maxValue = Math.max(...targets.value);
        const instance = new LinearCurve();

        const delta = maxValue - minValue;

        targets.value.forEach((target, i) => {
            const x = targets.time[i];
            const y = (target - minValue) / delta;
            instance.add({ x, y });
        });

        const description = instance.getCurveDescription();
        if (duration !== undefined) {
            description.duration = duration;
        }
        return {
            curve: description,
            drivenRange: [minValue, maxValue],
        };
    }
}
